import type { Form, FormField, FormInput } from '@/api/forms'
import type { PipelineDetail, Stage } from '@/api/types'

/** Input types offered for a free-form ("just collect") field on the public page. */
export const FIELD_TYPES = ['text', 'email', 'tel', 'number', 'date', 'textarea', 'select', 'radio', 'multiselect', 'checkbox']
export const inputCls =
  'px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Contact system slots a field can map to, with the public input type each implies. */
export const SYSTEM_CONTACT_FIELDS = [
  { key: 'first_name', label: 'First name', type: 'text' },
  { key: 'last_name', label: 'Last name', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'mobile', label: 'Mobile', type: 'tel' },
]
export const SYSTEM_KEYS = SYSTEM_CONTACT_FIELDS.map((s) => s.key)

/** Custom-field data types that render as a dropdown (options-backed). */
export const OPTION_DATA_TYPES = ['select', 'radio', 'multiselect']

/** Scalar custom-field types offerable in the inline "new field" creator (no options editor). */
export const NEW_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'multiline', label: 'Multi-line text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Checkbox' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
]

/** Maps a custom field's data type to the public form's input type. */
export function inputTypeForDataType(dt: string): string {
  switch (dt) {
    case 'select':
      return 'select'
    case 'radio':
      return 'radio'
    case 'multiselect':
      return 'multiselect'
    case 'number':
      return 'number'
    case 'date':
      return 'date'
    case 'boolean':
      return 'checkbox'
    case 'multiline':
      return 'textarea'
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    default:
      return 'text' // text, url
  }
}

/** Same slug rule as custom field keys: [a-z][a-z0-9_]*, max 50 chars. */
export function toFieldKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[0-9_]+/, '')
    .replace(/_+$/, '')
    .slice(0, 50)
}

export const DEFAULT_FIELDS: FormField[] = [
  { key: 'first_name', label: 'First name', type: 'text', required: true, entity: 'contact', custom: false },
  { key: 'email', label: 'Email', type: 'email', required: true, entity: 'contact', custom: false },
]

/** A field is "mapped" when it targets a system slot or a custom field (vs. just collected). */
export function isMapped(f: FormField): boolean {
  return !!f.custom || SYSTEM_KEYS.includes(f.key)
}

/** The <select> value encoding the field's current target. */
export function targetValue(f: FormField): string {
  if (f.custom) return `${f.entity === 'deal' ? 'd' : 'c'}:cf:${f.key}`
  if (SYSTEM_KEYS.includes(f.key)) return `c:sys:${f.key}`
  return 'collect'
}

/** Flattened, position-ordered stages of a pipeline (sections + loose). */
export function stagesOf(detail?: PipelineDetail): Stage[] {
  if (!detail) return []
  return [...detail.sections.flatMap((s) => s.stages), ...detail.looseStages].sort(
    (a, b) => a.position - b.position,
  )
}

/** Copy text to the clipboard, with a legacy fallback for non-secure contexts (plain http). */
export async function copyText(text: string): Promise<boolean> {
  // navigator.clipboard only exists in secure contexts (https / localhost).
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // fall through to the legacy path
    }
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(ta)
  return ok
}

/**
 * The snippet the agency pastes into its own website: the form in an iframe, plus a few lines that resize
 * the iframe to the form's height as it reports it (no empty space under a short form, no scrollbar in a
 * long one). Without the script — some site builders strip scripts — the iframe still works at 640px.
 */
export function embedSnippet(id: string, name: string): string {
  const url = `${window.location.origin}/f/${id}`
  const title = name.replace(/"/g, '&quot;')
  const frameId = `pp-form-${id}`
  return [
    `<iframe id="${frameId}" src="${url}" title="${title}" width="100%" height="640" loading="lazy" style="border:0;max-width:640px"></iframe>`,
    `<script>window.addEventListener("message",function(e){if(e.data&&e.data.ppFormId==="${id}"&&e.data.ppFormHeight){document.getElementById("${frameId}").style.height=e.data.ppFormHeight+"px"}})</script>`,
  ].join('\n')
}

/** When a submission arrived, e.g. "14 Aug 2026, 3:21 pm". */
export function submittedAt(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d
    .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()}`
}

/** Best name for the lead from what they actually submitted, falling back to their email. */
export function submitterLabel(data: Record<string, unknown>): string {
  const text = (k: string) => {
    const v = data[k]
    return typeof v === 'string' && v.trim() ? v.trim() : ''
  }
  const name = [text('first_name'), text('last_name')].filter(Boolean).join(' ')
  return name || text('email') || text('mobile') || text('phone') || 'Contact'
}


/** A submitted answer as text — multi-select answers arrive as arrays, and blanks read better as a dash. */
export function formatAnswer(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}


/**
 * Everything a form's save carries, from a saved form — for duplicating and for pausing/resuming from
 * the table. Every saved setting must be here: the backend replaces the whole form, so a field left out
 * is wiped (e.g. the after-submit steps).
 */
export function inputOf(f: Form, overrides: Partial<FormInput> = {}): FormInput {
  return {
    name: f.name,
    enabled: f.enabled,
    fields: f.fields.map((x) => ({ ...x })),
    createDeal: f.createDeal,
    dealPipelineId: f.dealPipelineId,
    ownerUserId: f.ownerUserId ?? null,
    dealStageId: f.dealStageId ?? null,
    dealNameTemplate: f.dealNameTemplate ?? null,
    description: f.description ?? null,
    thankYouMessage: f.thankYouMessage ?? null,
    redirectUrl: f.redirectUrl ?? null,
    notifyEmails: f.notifyEmails ?? [],
    hideTitle: f.hideTitle ?? false,
    style: f.style ?? {},
    submitLabel: f.submitLabel ?? null,
    addToListId: f.addToListId ?? null,
    autoReplyTemplateId: f.autoReplyTemplateId ?? null,
    enrolSequenceId: f.enrolSequenceId ?? null,
    listingDealId: f.listingDealId ?? null,
    ...overrides,
  }
}

/**
 * A copy of a form for another property: every occurrence of {@code from} (e.g. "39 Grand Parade,
 * Ashgrove") becomes {@code to} — in the name, the heading text, the thank-you message, field labels,
 * hidden values and the deal name. Case-insensitive; a blank {@code from} copies as-is.
 */
export function retarget(input: FormInput, from: string, to: string): FormInput {
  const needle = from.trim()
  if (!needle) return input
  const re = new RegExp(needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
  const sub = <T extends string | null | undefined>(v: T): T => (typeof v === 'string' ? (v.replace(re, to) as T) : v)
  return {
    ...input,
    name: sub(input.name),
    description: sub(input.description),
    thankYouMessage: sub(input.thankYouMessage),
    dealNameTemplate: sub(input.dealNameTemplate),
    submitLabel: sub(input.submitLabel),
    fields: input.fields.map((f) => ({ ...f, label: sub(f.label), value: sub(f.value) })),
  }
}
